<template>
    <promotion-details :promotionId="props.promotionId">
        <div>
            <page-header> Coupon Codes </page-header>

            <page-callout section="form-submission" class="mb-10" shadow />

            <vx-chart-categorical
                v-if="statistics && list.length"
                class="mb-10"
                :loading="loadingStatistics"
                :data="statisticsData"
                :colors="statisticsColors"
                :axis="{
                    x: {
                        key: 'count',
                    },
                }"
            >
                <template v-slot:title> Usage </template>
            </vx-chart-categorical>

            <promotion-coupons-controls
                v-if="list.length || !isEqual(defaultFilters(), fetchParams)"
                v-model="fetchParams"
                @reset="resetFilters"
                @apply="applyFilters"
                @import="importModal = true"
            ></promotion-coupons-controls>

            <page-loading v-if="loading && !list.length"></page-loading>

            <vx-box
                v-if="!loading && !list.length"
                color="info"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                This promotion doesn’t have codes yet.
                <vx-button @click="importModal = true">
                    Import Codes <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>

            <vx-table v-if="list.length" class="lg:grid-cols-6-auto mt-4" rowBreakpoint="lg">
                <vx-table-row isHeader>
                    <div>Coupon Code</div>
                    <div class="xl:col-span-2">Allocated to</div>
                    <div class="xl:col-span-2">Allocated by</div>
                    <div>Allocated at</div>
                </vx-table-row>

                <promotion-coupon-row
                    v-for="(item, index) in list"
                    :modelValue="list[index]"
                    :key="list[index].coupon_code"
                ></promotion-coupon-row>
            </vx-table>

            <div v-if="fetchParams.cursor" class="mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="lg"
                    class="grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                </vx-button>
            </div>
        </div>
        <promotion-coupons-import-modal
            v-model:visible="importModal"
            :promotionId="props.promotionId"
            @uploaded="onUploaded($event)"
        ></promotion-coupons-import-modal>
    </promotion-details>
</template>

<script setup>
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxChartCategorical, VxTable, VxTableRow } from '@voxie/frontend-components';
import { onMounted, ref, computed } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PromotionCouponsControls from '~/components/promotions/promotion-coupons/PromotionCouponsControls.vue';
import PromotionCouponsImportModal from '~/components/promotions/PromotionCouponsImportModal.vue';
import PromotionDetails from '~/components/promotions/PromotionDetails.vue';
import PromotionCouponRow from '~/components/promotions/promotion-coupons/PromotionCouponRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { promotionCouponsClient, promotionsClient } from '~/services/index';
import { isEqual } from '~/utils/helpers';
import { stringifyQueryString } from '~/utils/string';
import PageCallout from '~/components/general/PageCallout.vue';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    promotionId: {
        type: Number,
        required: true,
    },
});

const importModal = ref(false);

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => ({
    query: '',
    per_page: 10,
    sort_field: 'code',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: undefined,
    };

    loadData();
};

const loadingStatistics = ref(false);
const statistics = ref();
const statisticsData = computed(() => {
    return [
        {
            label: 'Allocated',
            count: statistics.value?.num_allocated || 0,
        },
        {
            label: 'Available',
            count: statistics.value?.num_available || 0,
        },
    ];
});
const statisticsColors = computed(() => {
    return [
        {
            iconColor: '#3B82F6',
            bgColor: '#93C5FD',
            label: 'Allocated',
        },
        {
            iconColor: '#64748b',
            bgColor: '#cbd5e1',
            label: 'Available',
        },
    ];
});

const loadStatistics = async () => {
    try {
        loadingStatistics.value = true;
        const response = await promotionsClient.getPromotionCouponsStatistics(teamId, props.promotionId);
        statistics.value = response.data;
    } finally {
        loadingStatistics.value = false;
    }
};

onMounted(() => {
    resetFilters();
    loadStatistics();
});

const loadData = () => {
    return fetchRecords(promotionCouponsClient.getPromotionCoupons(teamId, props.promotionId, fetchParams.value));
};

const onUploaded = (event) => {
    window.location.assign(
        `/settings/integrations/promotions/${props.promotionId}/coupons/imports?${stringifyQueryString({
            import_id: event.import_id,
            file_name: event.file_name,
            success: 1,
        })}`
    );
};
</script>
